import type { ReactNode } from 'react';

import { useContext, useCallback, createContext, createElement, useSyncExternalStore } from 'react';

import { media } from 'src/theme';

/**
 * Whether the window matches a media condition right now, following it as the window changes.
 * `query` is the condition itself, without the `@media` keyword — `(min-width: 1100px)`.
 *
 * The server has no window. It renders with `assumed`, and so does the render in which the browser
 * takes the server's markup over; the window's own answer follows at once.
 */
export const useMediaQuery = (query: string, assumed = false): boolean => {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = window.matchMedia(query);

      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query]
  );
  const matches = useCallback(() => window.matchMedia(query).matches, [query]);

  return useSyncExternalStore(subscribe, matches, () => assumed);
};

/**
 * `(min-width: 1100px)` — the `md` breakpoint of the tokens. From it the tab bar runs across the
 * top and a catalog shows its filters as a column beside the grid; below it both are a phone's.
 */
export const WIDE_QUERY = media.md.replace('@media ', '');

const WideHint = createContext(false);

/**
 * What the server takes the window for: wide unless the request came from a phone (`root.tsx`).
 * A laptop then gets its own layout in the first markup, instead of a phone's that jumps into
 * place once the script has run.
 */
export const WideHintProvider = ({ wide, children }: { wide: boolean; children: ReactNode }) =>
  createElement(WideHint.Provider, { value: wide }, children);

/** A laptop-wide window (the `md` breakpoint and up). */
export const useIsWide = (): boolean => useMediaQuery(WIDE_QUERY, useContext(WideHint));
